import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spline, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { StrandRow } from '@/components/twine/TwinePanel';
import { twineAPI, twineChanged } from '@/lib/twineAPI';

const recordPath = (subject) => {
  if (!subject) return null;
  if (subject.type === 'contact') return `/contacts?open=${subject.id}`;
  if (subject.type === 'interview') return `/interviews/${subject.id}`;
  return null;
};

// Every suggestion waiting for a person, grouped by record
export default function TwineReview() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => twineAPI.review().then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const groups = {};
  (rows || []).forEach((s) => {
    const k = `${s.subject?.type}:${s.subject?.id}`;
    (groups[k] = groups[k] || { subject: s.subject, items: [] }).items.push(s);
  });

  const keepAll = async (items) => {
    try {
      for (const s of items) await twineAPI.update(s.id, { status: 'kept' }); // eslint-disable-line no-await-in-loop
      toast.success(`Kept ${items.length}`); twineChanged(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not keep all'); load(); }
  };

  return (
    <Layout title="The Twine">
      <div className="max-w-3xl">
        <h1 className="text-2xl font-semibold text-[#1E2A33] flex items-center gap-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
          <Spline className="w-6 h-6 text-[#A62B1F]" />The Twine
        </h1>
        <p className="text-sm text-[#5C6670] mt-1 mb-5">
          Like mending the nets between trips: what was learned in yarns, interviews and desk work, tied back onto the right record
          so nobody has to ask twice. Nothing here is remembered until someone on the team keeps it.
        </p>
        {rows === null ? <p className="text-sm text-[#5C6670]">Loading…</p> : rows.length === 0 ? (
          <div className="rounded-xl border border-[#E3DDD1] bg-white p-10 text-center">
            <Check className="w-8 h-8 mx-auto text-emerald-700 mb-2" />
            <p className="font-semibold">All mended</p>
            <p className="text-sm text-[#5C6670]">New suggestions show up here when a yarn is archived, an interview is approved or the desk finishes a job.</p>
          </div>
        ) : Object.values(groups).map((g) => (
          <div key={`${g.subject?.type}:${g.subject?.id}`} className="rounded-xl border border-[#E3DDD1] bg-white p-4 md:p-5 mb-4">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#5C6670]">{g.subject?.type}</div>
                {recordPath(g.subject)
                  ? <Link to={recordPath(g.subject)} className="font-semibold text-[#1E2A33] hover:underline">{g.subject?.label}</Link>
                  : <span className="font-semibold text-[#1E2A33]">{g.subject?.label}</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => keepAll(g.items)}><Check className="w-4 h-4 mr-1" />Keep all {g.items.length}</Button>
            </div>
            <ul className="divide-y divide-[#E3DDD1]">{g.items.map((s) => <StrandRow key={s.id} s={s} onChanged={load} />)}</ul>
          </div>
        ))}
      </div>
    </Layout>
  );
}
